import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  signal,
  WritableSignal,
} from '@angular/core';
import { DefaultFilterComponent } from './default-filter.component';
import { ComboboxItemFilter } from '@shared/abstraction/combobox/combobox-item-filter';
import { KeyValue } from '@angular/common';
import { map, Observable, tap } from 'rxjs';
import { DropdownOptions } from '../dropdown-filter/dropdown-options.interface';

@Component({
  standalone: false,
  template: '',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BaseDropdownComboboxComponent
  extends DefaultFilterComponent<ComboboxItemFilter | ComboboxItemFilter[]>
  implements OnInit
{
  options: WritableSignal<ComboboxItemFilter[]> = signal([]);
  selectedItems: ComboboxItemFilter[] = [];
  optionsSettingResolver!: (options: ComboboxItemFilter[]) => void;

  get optionsType(): string {
    return this.filterColumn().getOptions<DropdownOptions>().isMultiple
      ? 'multiple'
      : 'single';
  }

  override ngOnInit(): void {
    const itemsSource = this.filterColumn().getOptions<DropdownOptions>().items;

    if (itemsSource instanceof Observable) {
      this.setAsyncData(itemsSource);
    }

    super.ngOnInit();

    if (!(itemsSource instanceof Observable)) {
      const options = this.commonProcess(itemsSource);
      this.optionsSettingResolver(options);
    }

    if (!this.value()) {
      this.value.set([]);
    }
  }

  updateModelHandler(): void {
    this.selectedItems = this.getSelectedValue();
    this.update(this.selectedItems);
  }

  protected getSelectedValue(): ComboboxItemFilter[] {
    return this.filterColumn().getOptions<DropdownOptions>().isMultiple
      ? this.getValue<ComboboxItemFilter[]>()
      : [this.getValue<ComboboxItemFilter>()];
  }

  protected clearValue(
    selectedItems: ComboboxItemFilter | ComboboxItemFilter[]
  ): void {
    if (Array.isArray(selectedItems)) {
      selectedItems.forEach((item: ComboboxItemFilter) => {
        item.checked = false;
      });
    } else {
      this.utils.parseTo<ComboboxItemFilter>(selectedItems).checked = false;
    }

    this.selectedItems = [];
    this.value.set([]);
  }

  protected checkSelectedStatus(options: ComboboxItemFilter[]): void {
    if (this.selectedItems.length > 0) {
      options
        .filter((item: ComboboxItemFilter) =>
          this.selectedItems.some(
            (selected: ComboboxItemFilter) => selected.key === item.key
          )
        )
        .forEach((item: ComboboxItemFilter) => {
          item.checked = true;
        });
    }
  }

  private commonProcess(
    options: KeyValue<string, string>[]
  ): ComboboxItemFilter[] {
    const items = this.utils.parseTo<ComboboxItemFilter[]>(options);

    if (this.value() && this.getValue<ComboboxItemFilter[]>().length > 0) {
      this.selectedItems = this.getValue<ComboboxItemFilter[]>();
      this.checkSelectedStatus(items);

      if (this.filterColumn().getOptions<DropdownOptions>().isMultiple) {
        const value = this.getValue<ComboboxItemFilter[]>();
        this.value.set(value[0]);
      }
    }

    return items;
  }

  private setAsyncData(
    asyncFilterOptions: Observable<KeyValue<string, string>[]>
  ): void {
    this.containAsyncData$ = asyncFilterOptions?.pipe(
      map((options: KeyValue<string, string>[]) => this.commonProcess(options)),
      tap((items: ComboboxItemFilter[]) => this.optionsSettingResolver(items))
    );
  }
}
